/**
 * @fileoverview Componente responsável por renderizar um card individual no quadro kanban.
 */

import React, { useRef } from "react";
import useTouchDrag from "./useTouchDrag";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import { MessageCircle, Phone, FileText, MessageSquare, CheckSquare } from "lucide-react";

import { UserAvatar } from "../LeadShared";
import { i18n } from "../../translate/i18n";

const PriorityIcon = ({ type, size = 16 }) => {
  const s = size;
  const props = { width: s, height: s, viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg" };
  const stroke = "currentColor";
  const sw = 1.8;

  if (type === "urgent") {
    return (
      <svg {...props}>
        <path d="M8 11V5" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
        <path d="M5 7l3-3 3 3" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 11l3-3 3 3" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === "high") {
    return (
      <svg {...props}>
        <path d="M8 12V4" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
        <path d="M4.5 7.5L8 4l3.5 3.5" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === "low") {
    return (
      <svg {...props}>
        <path d="M8 4v8" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
        <path d="M4.5 8.5L8 12l3.5-3.5" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg {...props}>
      <path d="M4 8h8" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
    </svg>
  );
};

const PRIORITIES = {
  urgent: { label: "Maior", color: "#dc2626" },
  high:   { label: "Alta",  color: "#f59e0b" },
  normal: { label: "Média", color: "#3b82f6" },
  low:    { label: "Baixa", color: "#22c55e" },
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    card: {
      fontFamily: '"Montserrat", "Inter", sans-serif',
      background: dark ? "#101218" : "#FFFFFF",
      border: dark
        ? "1px solid rgba(255,255,255,0.05)"
        : "1px solid rgba(15,23,42,0.06)",
      borderRadius: 10,
      padding: "11px 12px 10px",
      marginBottom: 8,
      cursor: "pointer",
      transition: "border-color .15s, box-shadow .15s, transform .15s",
      position: "relative",
      userSelect: "none",
      WebkitTouchCallout: "none",
      // Allow both directions of native scrolling over the card.
      // The long-press hook calls preventDefault on touchmove only AFTER drag starts,
      // so a quick swipe stays a scroll (board horizontally, column vertically).
      touchAction: "pan-x pan-y",
      overflow: "hidden",
      boxShadow: dark
        ? "0 1px 2px rgba(0,0,0,0.35)"
        : "0 1px 2px rgba(15,23,42,0.04)",
      "&:hover": {
        borderColor: dark ? "rgba(99,102,241,0.20)" : "rgba(61,92,253,0.20)",
        boxShadow: dark
          ? "0 4px 14px -4px rgba(0,0,0,0.55)"
          : "0 4px 14px -2px rgba(15,23,42,0.10), 0 0 0 1px rgba(61,92,253,0.06)",
        transform: "translateY(-1px)",
      },
    },
    coverImage: {
      width: "calc(100% + 24px)",
      height: 110,
      objectFit: "cover",
      display: "block",
      margin: "-11px -12px 10px -12px",
      borderRadius: "10px 10px 0 0",
    },
    cardDragging: {
      opacity: 0.55,
      transform: "rotate(1.5deg) scale(1.01)",
      cursor: "grabbing",
      boxShadow: dark
        ? "0 18px 40px -8px rgba(0,0,0,0.65), 0 0 0 1px rgba(99,102,241,0.25)"
        : "0 18px 40px -8px rgba(15,23,42,0.25), 0 0 0 1px rgba(61,92,253,0.32)",
    },
    cardTouchDragging: {
      transform: "rotate(1.5deg) scale(1.02)",
      transition: "transform 0.15s",
      boxShadow: dark
        ? "0 18px 40px -8px rgba(0,0,0,0.65), 0 0 0 1px rgba(99,102,241,0.25)"
        : "0 18px 40px -8px rgba(15,23,42,0.25), 0 0 0 1px rgba(61,92,253,0.32)",
    },

    unreadDot: {
      position: "absolute",
      top: 10,
      right: 10,
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: "#22c55e",
      boxShadow: "0 0 0 3px rgba(34,197,94,0.18), 0 0 6px rgba(34,197,94,0.5)",
    },

    topRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 8,
      marginBottom: 6,
    },
    name: {
      fontSize: "0.86rem",
      fontWeight: 600,
      letterSpacing: "-0.005em",
      color: dark ? "#E8EAED" : "#0F172A",
      lineHeight: 1.32,
      flex: 1,
      wordBreak: "break-word",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },

    body: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
      marginBottom: 8,
    },
    phone: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: "0.76rem",
      color: dark ? "#9CA3AF" : "#475569",
      cursor: "pointer",
      borderRadius: 5,
      padding: "2px 6px",
      margin: "-2px -6px",
      transition: "background .12s, color .12s",
      width: "fit-content",
      maxWidth: "100%",
      "&:hover": {
        background: dark ? "rgba(37,211,102,0.08)" : "rgba(37,211,102,0.08)",
        color: "#25D366",
      },
    },
    phoneIcon: { color: "#25D366", flexShrink: 0 },
    phoneNoClick: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: "0.74rem",
      color: dark ? "#6B7280" : "#94A3B8",
    },
    serviceOrder: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: "0.72rem",
      color: "#22c55e",
      fontWeight: 600,
    },
    dynamicField: {
      display: "flex",
      alignItems: "baseline",
      gap: 5,
      fontSize: "0.75rem",
      overflow: "hidden",
    },
    fieldLabel: {
      fontSize: "0.68rem",
      fontWeight: 600,
      letterSpacing: "0.01em",
      color: dark ? "#6B7280" : "#94A3B8",
      flexShrink: 0,
      whiteSpace: "nowrap",
    },
    fieldValue: {
      fontSize: "0.75rem",
      color: dark ? "#D1D5DB" : "#334155",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },

    checklistRow: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      marginBottom: 6,
    },
    checklistIcon: {
      flexShrink: 0,
      color: dark ? "#6B7280" : "#94A3B8",
      display: "flex",
    },
    checklistBar: {
      flex: 1,
      height: 4,
      borderRadius: 2,
      backgroundColor: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.06)",
      overflow: "hidden",
    },
    checklistBarFill: {
      height: "100%",
      borderRadius: 2,
      transition: "width 0.3s ease",
    },
    checklistCount: {
      fontSize: "0.66rem",
      fontWeight: 600,
      color: dark ? "#9CA3AF" : "#94A3B8",
      whiteSpace: "nowrap",
      flexShrink: 0,
      fontVariantNumeric: "tabular-nums",
    },

    footer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 8,
      paddingTop: 8,
      borderTop: dark
        ? "1px solid rgba(255,255,255,0.04)"
        : "1px solid rgba(15,23,42,0.04)",
    },
    footerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      minWidth: 0,
    },
    footerRight: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexShrink: 0,
    },
    time: {
      fontSize: "0.7rem",
      color: dark ? "#6B7280" : "#94A3B8",
      fontVariantNumeric: "tabular-nums",
    },
    priorityBadge: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 20,
      height: 20,
      borderRadius: 5,
      lineHeight: 1,
      flexShrink: 0,
    },
    slaBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 3,
      padding: "2px 6px",
      borderRadius: 5,
      fontSize: "0.64rem",
      fontWeight: 700,
      lineHeight: 1,
      border: "1px solid",
      whiteSpace: "nowrap",
      fontVariantNumeric: "tabular-nums",
    },
    commentBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 3,
      fontSize: "0.66rem",
      fontWeight: 600,
      color: dark ? "#9CA3AF" : "#94A3B8",
    },
  };
});

const formatTimeAgo = (date) => {
  if (!date) return "-";
  const now = new Date();
  const createdAt = new Date(date);
  const diffMs = now - createdAt;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return "Agora";
  if (diffMinutes < 60) return `${diffMinutes}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 30) return `${diffDays}d`;
  return new Date(date).toLocaleDateString("pt-BR");
};

const KanbanCard = ({ lead, statusId, onView, onStartConversation }) => {
  const classes = useStyles();
  const isUnread = !lead.viewedAt;
  const desktopDragRef = useRef(false);
  const touchDrag = useTouchDrag({ leadId: lead.id, statusId });

  const handleDragStart = (e) => {
    desktopDragRef.current = true;
    e.dataTransfer.setData("leadId", lead.id);
    e.dataTransfer.setData("fromStatusId", statusId);
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.classList.add(classes.cardDragging);
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove(classes.cardDragging);
  };

  const handleClick = (e) => {
    if (desktopDragRef.current || touchDrag.didDragRef.current) {
      desktopDragRef.current = false;
      return;
    }
    onView(lead);
  };

  const handleMouseDown = () => {
    desktopDragRef.current = false;
  };

  const coverUrl = lead.customFields?.coverUrl;
  const hasDynamicFields = lead.customFields?.fields?.length > 0;
  const visibleFields = hasDynamicFields
    ? lead.customFields.fields.filter((f) => f.showOnCard && f.value)
    : [];

  // Checklist progress
  const checklists = lead.customFields?.fields?.filter(
    (f) => f.type === "checklist" && f.items?.length > 0
  ) || [];
  const checklistInfo = (() => {
    if (checklists.length === 0) return null;
    const allItems = checklists.flatMap((f) => f.items);
    const total = allItems.length;
    const done = allItems.filter((c) => c.done).length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return { total, done, pct };
  })();

  // SLA badge data
  const slaInfo = (() => {
    const slaIso = lead.customFields?.slaDate;
    if (!slaIso) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(slaIso);
    target.setHours(0, 0, 0, 0);
    const diff = Math.round((target - today) / 86400000);
    if (diff < 0) {
      return {
        color: "#ef4444",
        bg: "rgba(239,68,68,0.14)",
        border: "rgba(239,68,68,0.32)",
        label: diff === -1 ? "-1d" : `${diff}d`,
        tooltip: diff === -1 ? "Vencido há 1 dia" : `Vencido há ${Math.abs(diff)} dias`,
      };
    }
    if (diff === 0) {
      return {
        color: "#f59e0b",
        bg: "rgba(245,158,11,0.18)",
        border: "rgba(245,158,11,0.36)",
        label: "Hoje",
        tooltip: "Vence hoje",
      };
    }
    if (diff <= 3) {
      return {
        color: "#f59e0b",
        bg: "rgba(245,158,11,0.12)",
        border: "rgba(245,158,11,0.28)",
        label: `${diff}d`,
        tooltip: diff === 1 ? "Vence amanhã" : `Vence em ${diff} dias`,
      };
    }
    return {
      color: "#22c55e",
      bg: "rgba(34,197,94,0.10)",
      border: "rgba(34,197,94,0.24)",
      label: `${diff}d`,
      tooltip: `${diff} dias até o prazo`,
    };
  })();

  return (
    <div
      ref={touchDrag.cardRef}
      className={`${classes.card}${touchDrag.isDragging ? ` ${classes.cardTouchDragging}` : ""}`}
      draggable
      onMouseDown={handleMouseDown}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
    >
      {coverUrl && (
        <img
          src={coverUrl}
          alt=""
          className={classes.coverImage}
          onError={(e) => { e.target.style.display = "none"; }}
        />
      )}
      {isUnread && <div className={classes.unreadDot} />}

      <div className={classes.topRow}>
        <Typography className={classes.name} title={lead.name}>
          {lead.name}
        </Typography>
      </div>

      <div className={classes.body}>
        {hasDynamicFields ? (
          visibleFields.map((field) => {
            if (field.type === "whatsapp") {
              return (
                <div
                  key={field.id}
                  className={classes.phone}
                  onClick={(e) => {
                    e.stopPropagation();
                    onStartConversation(field.value);
                  }}
                  title={i18n.t("externalLeads.card.startConversation")}
                >
                  <MessageCircle size={13} className={classes.phoneIcon} strokeWidth={2} />
                  {field.value}
                </div>
              );
            }
            if (field.type === "currency") {
              const num = parseFloat(field.value || 0);
              const hasCents = num % 1 !== 0;
              const formatted = num.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
                minimumFractionDigits: hasCents ? 2 : 0,
                maximumFractionDigits: 2,
              });
              return (
                <div key={field.id} className={classes.dynamicField}>
                  <Typography className={classes.fieldLabel}>{field.label}:</Typography>
                  <Typography className={classes.fieldValue}>{formatted}</Typography>
                </div>
              );
            }
            return (
              <div key={field.id} className={classes.dynamicField}>
                <Typography className={classes.fieldLabel}>{field.label}:</Typography>
                <Typography className={classes.fieldValue}>{field.value}</Typography>
              </div>
            );
          })
        ) : (
          <>
            {lead.phone ? (
              <div
                className={classes.phone}
                onClick={(e) => {
                  e.stopPropagation();
                  onStartConversation(lead.phone);
                }}
                title={i18n.t("externalLeads.card.startConversation")}
              >
                <MessageCircle size={13} className={classes.phoneIcon} strokeWidth={2} />
                {lead.phone}
              </div>
            ) : (
              <div className={classes.phoneNoClick}>
                <Phone size={12} strokeWidth={1.75} />
                {i18n.t("externalLeads.card.notInformed")}
              </div>
            )}
            {lead.serviceOrder && (
              <div className={classes.serviceOrder}>
                <FileText size={12} strokeWidth={1.75} />
                O.S. {lead.serviceOrder}
              </div>
            )}
          </>
        )}
      </div>

      {checklistInfo && (
        <div className={classes.checklistRow}>
          <span className={classes.checklistIcon}>
            <CheckSquare size={12} strokeWidth={1.75} />
          </span>
          <div className={classes.checklistBar}>
            <div
              className={classes.checklistBarFill}
              style={{
                width: `${checklistInfo.pct}%`,
                backgroundColor: checklistInfo.pct === 100 ? "#22c55e" : "#3b82f6",
              }}
            />
          </div>
          <Typography className={classes.checklistCount}>
            {checklistInfo.done}/{checklistInfo.total}
          </Typography>
        </div>
      )}

      <div className={classes.footer}>
        <div className={classes.footerLeft}>
          <Tooltip title={new Date(lead.createdAt).toLocaleString("pt-BR")} placement="bottom">
            <Typography className={classes.time}>
              {formatTimeAgo(lead.createdAt)}
            </Typography>
          </Tooltip>
        </div>
        <div className={classes.footerRight}>
          {lead.customFields?.comments?.length > 0 && (
            <Tooltip title={`${lead.customFields.comments.length} comentário(s)`} placement="top">
              <span className={classes.commentBadge}>
                <MessageSquare size={11} strokeWidth={1.75} />
                {lead.customFields.comments.length}
              </span>
            </Tooltip>
          )}
          {slaInfo && (
            <Tooltip title={slaInfo.tooltip} placement="top">
              <span
                className={classes.slaBadge}
                style={{
                  background: slaInfo.bg,
                  color: slaInfo.color,
                  borderColor: slaInfo.border,
                }}
              >
                {slaInfo.label}
              </span>
            </Tooltip>
          )}
          {lead.priority && PRIORITIES[lead.priority] && (
            <Tooltip title={`Prioridade: ${PRIORITIES[lead.priority].label}`} placement="top">
              <span
                className={classes.priorityBadge}
                style={{
                  backgroundColor: `${PRIORITIES[lead.priority].color}1F`,
                  color: PRIORITIES[lead.priority].color,
                }}
              >
                <PriorityIcon type={lead.priority} size={13} />
              </span>
            </Tooltip>
          )}
          {lead.assignedUser && (
            <Tooltip title={lead.assignedUser.name} placement="top">
              <span style={{ display: "inline-flex" }}>
                <UserAvatar user={lead.assignedUser} size={22} />
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

export default KanbanCard;
